// A project's meetings: start one now or schedule the review call (the team and, if wanted, the client get a calendar
// invitation), and what came out of each - its recordings and transcripts, for whoever could open them anyway.
import { useState, type FormEvent } from "react";
import { Link } from "react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ApiError, api } from "../../lib/api";
import { formatDuration } from "../../lib/calls";
import { formatShort } from "../../lib/chat";
import { projectKeys, type Project, type ProjectMeeting } from "../../lib/projects";
import { Alert, Badge, Button, Card, Dialog, Field, Select, Spinner } from "../../components/ui";
import { Icon } from "../../components/icons";
import { errorText, useOrg } from "../org";

const when = (iso: string) => new Date(iso).toLocaleString([], { weekday: "short", day: "numeric", month: "short", hour: "numeric", minute: "2-digit" });

export function ProjectMeetings({ project }: { project: Project }) {
  const { org } = useOrg();
  const [scheduling, setScheduling] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState<string | null>(null);
  const list = useQuery({
    queryKey: projectKeys.meetings(org.slug, project.id),
    queryFn: () => api<{ meetings: ProjectMeeting[] }>(`/orgs/${org.slug}/projects/${project.id}/meetings`).then((r) => r.meetings),
    refetchInterval: 15_000,   // who is in a meeting right now
  });

  async function startNow() {
    setBusy(true); setError(null);
    try {
      const r = await api<{ meeting: { code: string } }>(`/orgs/${org.slug}/projects/${project.id}/meetings`, { method: "POST", body: { kind: "instant" } });
      window.location.assign(`/m/${r.meeting.code}`);
    } catch (err) { setError(errorText(err)); setBusy(false); }
  }
  async function copy(m: ProjectMeeting) {
    try { await navigator.clipboard.writeText(m.url); setCopied(m.id); setTimeout(() => setCopied(null), 2000); } catch { window.prompt("Copy the meeting link:", m.url); }
  }

  const meetings = list.data ?? [];
  const now = Date.now();
  const upcoming = meetings.filter((m) => m.startsAt && m.endsAt && new Date(m.endsAt).getTime() > now).sort((a, b) => a.startsAt!.localeCompare(b.startsAt!));
  const rest = meetings.filter((m) => !upcoming.includes(m));

  return (
    <div className="space-y-5 pb-6">
      {project.can.meet && (
        <div className="flex flex-wrap items-center gap-2">
          <Button onClick={startNow} loading={busy}><Icon name="video" className="h-4 w-4" />Start a meeting now</Button>
          <Button variant="secondary" onClick={() => setScheduling(true)}><Icon name="calendar" className="h-4 w-4" />Schedule</Button>
          <span className="text-sm text-slate-500">Its recording and transcript are kept here, with the project.</span>
        </div>
      )}
      {error && <Alert>{error}</Alert>}
      {list.isLoading && <div className="flex justify-center p-10 text-brand-600"><Spinner /></div>}
      {list.data && meetings.length === 0 && (
        <Card className="p-10 text-center">
          <div className="mx-auto mb-3 grid h-12 w-12 place-items-center rounded-2xl bg-brand-50 text-brand-600 dark:bg-brand-700/30"><Icon name="video" /></div>
          <p className="font-medium">No meetings yet</p>
          <p className="mt-1 text-sm text-slate-500">{project.can.meet ? "Hold the kick-off or the review call from here, and turn what's said into tasks." : "Meetings the team holds for this project show up here."}</p>
        </Card>
      )}
      {upcoming.length > 0 && <MeetingGroup title="Coming up" meetings={upcoming} copied={copied} onCopy={copy} />}
      {rest.length > 0 && <MeetingGroup title={upcoming.length ? "Earlier" : "Meetings"} meetings={rest} copied={copied} onCopy={copy} />}
      {scheduling && <ScheduleDialog project={project} onClose={() => setScheduling(false)} />}
    </div>
  );
}

function MeetingGroup({ title, meetings, copied, onCopy }: { title: string; meetings: ProjectMeeting[]; copied: string | null; onCopy: (m: ProjectMeeting) => void }) {
  const { org } = useOrg();
  return (
    <section>
      <h2 className="mb-2 text-sm font-semibold uppercase tracking-wide text-slate-500">{title}</h2>
      <Card className="p-0">
        <ul className="divide-y divide-slate-100 dark:divide-slate-800">
          {meetings.map((m) => (
            <li key={m.id} className="px-5 py-4">
              <div className="flex flex-wrap items-center gap-3">
                <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-brand-50 text-brand-600 dark:bg-brand-700/30"><Icon name="video" /></span>
                <div className="min-w-0 flex-1">
                  <p className="flex flex-wrap items-center gap-2 font-medium"><span className="min-w-0 break-words">{m.title}</span>{m.live && <Badge tone="brand">Live · {m.live.people}</Badge>}</p>
                  <p className="text-sm text-slate-500">
                    {m.startsAt ? when(m.startsAt) : `Started ${formatShort(m.createdAt)}`}
                    {m.host ? ` · ${m.host.name}` : ""}
                  </p>
                </div>
                {m.open && (
                  <div className="flex w-full shrink-0 flex-wrap gap-1 sm:w-auto">
                    <Button size="sm" variant="ghost" onClick={() => onCopy(m)}><Icon name="link" className="h-4 w-4" />{copied === m.id ? "Copied" : "Copy link"}</Button>
                    <a href={`/m/${m.code}`}><Button size="sm" variant={m.live ? "primary" : "secondary"}>{m.live ? "Join" : "Open"}</Button></a>
                  </div>
                )}
              </div>
              {(m.recordings.length > 0 || m.transcripts.length > 0) && (
                <ul className="mt-3 flex flex-wrap gap-2 pl-[3.25rem] text-sm">
                  {m.transcripts.map((t) => (
                    <li key={t.id}>
                      <Link to={`/o/${org.slug}/transcripts/${t.id}`} className="inline-flex items-center gap-1.5 rounded-lg bg-slate-100 px-2.5 py-1 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700">
                        <Icon name="file" className="h-4 w-4" />Transcript{t.wordCount ? ` · ${t.wordCount.toLocaleString()} words` : ""}
                      </Link>
                    </li>
                  ))}
                  {m.recordings.map((r) => (
                    <li key={r.id}>
                      <Link to={`/o/${org.slug}/recordings`} className="inline-flex items-center gap-1.5 rounded-lg bg-slate-100 px-2.5 py-1 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700">
                        <Icon name="record" className="h-4 w-4" />Recording{r.durationSeconds ? ` · ${formatDuration(r.durationSeconds)}` : r.status !== "ready" ? " · processing" : ""}
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </li>
          ))}
        </ul>
      </Card>
    </section>
  );
}

function ScheduleDialog({ project, onClose }: { project: Project; onClose: () => void }) {
  const { org } = useOrg();
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fields, setFields] = useState<Record<string, string>>({});
  const tomorrow = new Date(Date.now() + 86_400_000);
  const day = `${tomorrow.getFullYear()}-${String(tomorrow.getMonth() + 1).padStart(2, "0")}-${String(tomorrow.getDate()).padStart(2, "0")}`;
  const hasClients = project.clients.length > 0;

  async function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const d = new FormData(e.currentTarget);
    setBusy(true); setError(null); setFields({});
    try {
      await api(`/orgs/${org.slug}/projects/${project.id}/meetings`, {
        method: "POST",
        body: {
          kind: "scheduled", title: String(d.get("title") ?? "").trim(),
          start: `${d.get("date")}T${d.get("time")}`, timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          durationMinutes: Number(d.get("duration")), inviteTeam: d.get("inviteTeam") === "on", inviteClients: d.get("inviteClients") === "on",
        },
      });
      await qc.invalidateQueries({ queryKey: projectKeys.meetings(org.slug, project.id) });
      onClose();
    } catch (err) {
      setError(errorText(err));
      if (err instanceof ApiError) setFields(err.fields);
      setBusy(false);
    }
  }
  return (
    <Dialog open onClose={onClose} title="Schedule a meeting">
      <form onSubmit={submit} className="space-y-4" noValidate>
        {error && <Alert>{error}</Alert>}
        <Field label="Title" name="title" maxLength={120} defaultValue={hasClients ? `${project.name}: review` : project.name} required />
        <div className="grid gap-4 sm:grid-cols-3">
          <Field label="Date" name="date" type="date" defaultValue={day} required error={fields.start} />
          <Field label="Time" name="time" type="time" defaultValue="10:00" required />
          <Select label="Length" name="duration" defaultValue="30">
            {[15, 30, 45, 60, 90, 120].map((m) => <option key={m} value={m}>{m < 60 ? `${m} min` : `${m / 60} h${m % 60 ? " 30" : ""}`}</option>)}
          </Select>
        </div>
        <fieldset className="space-y-2 text-sm">
          <legend className="mb-1 font-medium text-slate-700 dark:text-slate-300">Send a calendar invitation to</legend>
          <label className="flex items-center gap-2"><input type="checkbox" name="inviteTeam" defaultChecked className="h-4 w-4 rounded" />The project's team ({project.members.length})</label>
          {hasClients && <label className="flex items-center gap-2"><input type="checkbox" name="inviteClients" defaultChecked className="h-4 w-4 rounded" />The client ({project.clients.map((c) => c.name).join(", ")})</label>}
        </fieldset>
        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
          <Button type="submit" loading={busy}>Schedule</Button>
        </div>
      </form>
    </Dialog>
  );
}
